<script setup lang="ts">
import { computed, useSlots, type InputHTMLAttributes } from 'vue';
import vSelect, { VueSelectData, VueSelectProps } from 'vue-select';
import CButton from '../CButton';
import type { CSelectItem, Primitive } from '.';

const props = defineProps<{
    value?: CSelectItem<Primitive> | Primitive;
    options: CSelectItem<Primitive>[];
    id?: InputHTMLAttributes['id'];
    label?: string;
    labelClass?: InputHTMLAttributes['class'];
    modelTransform?: VueSelectProps['reduce'];
    alwaysOpen?: boolean;
    disabled?: boolean;
    placeholder?: VueSelectProps['placeholder'];
    loading?: VueSelectProps['loading'];
    searchable?: boolean;
    filterable?: boolean;
    clearable?: boolean;
    appendToBody?: boolean;
    groupValues?: string;
    groupLabel?: string;
}>();

const emit = defineEmits<{
    (event: 'input', value?: CSelectItem<Primitive> | Primitive): void;
    (event: 'open'): void;
    (event: 'close'): void;
    (event: 'search', val: string): void;
    (event: 'delete-option', value: CSelectItem<Primitive>): void;
}>();

const model = computed({
    get: () => props.value,
    set: (value?: CSelectItem<Primitive> | Primitive) => emit('input', value)
});
const slots = useSlots();

const dropdownShouldOpen = ({ open, mutableLoading }: VueSelectData) => {
    return props.alwaysOpen || open && !mutableLoading;
};

const isOptionSelectable: VueSelectProps['selectable'] = (item) => !(item as CSelectItem)?.disabled;
</script>

<template>
    <div class="c-select-wrapper">
        <label v-if="label" class="c-select-label" :class="labelClass" :for="id"> {{ label }} </label>
        <v-select
            v-model="model"
            aria-placeholder="Select type"
            :options="options"
            :searchable="searchable"
            :clearable="clearable"
            :filterable="filterable"
            :placeholder="placeholder"
            :dropdown-should-open="dropdownShouldOpen"
            :loading="loading"
            :reduce="modelTransform"
            :selectable="isOptionSelectable"
            :disabled="disabled"
            :append-to-body="appendToBody"
            :group-values="groupValues"
            :group-label="groupLabel"
            @search="emit('search', $event)"
            @open="emit('open')"
            @close="emit('close')"
        >
            <template #open-indicator="{ attributes }">
                <span v-bind="attributes">
                    <font-awesome-icon icon="fas fa-angle-down" />
                </span>
            </template>
            <template #option="option">
                <div class="d-flex justify-content-between align-items-center">
                    {{ option.label }}
                    <c-button
                        v-if="option.deletable"
                        class="ml-2"
                        size="icon"
                        :icon="['far', 'trash-can']"
                        :disabled="option.disabled"
                        @click.stop="emit('delete-option', option)"
                    />
                </div>
            </template>
            <!-- override slots for cases where custom renders are needed -->
            <template v-for="(_, slotName) of slots" v-slot:[slotName]="scope">
                <slot :name="slotName" v-bind="scope" />
            </template>
        </v-select>
    </div>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.c-select-wrapper {
    .c-select-label {
        font-size: 0.875rem;
        margin-bottom: 0.19rem;
    }
    :deep() {
        .vs__dropdown-menu,
        .vs__dropdown-toggle {
            border: 1px solid #ccc;
            padding: 0;
            input {
                &::placeholder {
                    color: get-color('gray');
                }
            }
        }
        .vs__dropdown-menu {
            min-width: fit-content;
            top: calc(100% + 3px);
        }

        .vs__selected {
            margin: 0;
        }
        .vs__dropdown-option {
            min-width: fit-content;
            padding: 0.5rem;
            border-bottom: 1px solid #ccc;

            &--highlight {
                background: #f0f0f0;
                color: #000;
            }
            &:last-child {
                border-bottom: none;
            }
        }
        &.vs--open .vs__dropdown-toggle {
            border-bottom-color: #ccc;
        }
        &.vs--single.vs--open .vs__selected {
            position: relative;
            opacity: 1;
        }
        .vs__actions {
            padding: 0;
            padding-right: 0.75rem;
        }
        .vs__dropdown-toggle {
            height: 2.375rem;
        }
    }
}
</style>
